"use client"

import { FC, useState, useEffect, useRef, useCallback, useMemo } from "react"
import { usePathname } from "next/navigation"
import { useTranslations } from "next-intl"
import { useMutation } from "@tanstack/react-query"
import { useRouter } from "utils"
import debounce from "debounce"
import CloseIcon from "../../icons/CloseIcon"
import SearchIcon from "../../icons/SearchIcon"
import Results from "./Results"
import EmptyState from "./EmptyState"
import {
  Overlay,
  SearchContainer,
  CloseButton,
  MobileCloseButton,
  SearchPanel,
  SearchInputWrapper,
  StyledInput,
  LoadingWrapper,
} from "./styles"

export interface SearchResult {
  title: string
  description?: string
  url: string
  thumbnail?: string
}

interface SearchResponse {
  results: SearchResult[]
  total: number
}

interface SearchProps {
  open: boolean
  onClose: () => void
  locale: string
  /**
   * EGDE-346: placeholder for the site-search input, authored on the Header
   * component in the CMS. Falls back to the default translation when empty.
   */
  placeholder?: string | null
}

async function fetchSearchResults(
  query: string,
  locale: string,
): Promise<SearchResponse> {
  const params = new URLSearchParams({ q: query, locale })
  const res = await fetch(`/app-api/search?${params.toString()}`)
  if (!res.ok) {
    throw new Error(`Search failed: ${res.status}`)
  }
  return res.json()
}

const Search: FC<SearchProps> = ({ open, onClose, locale, placeholder }) => {
  const t = useTranslations("Homepage")
  const router = useRouter()
  const [query, setQuery] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)
  const pathname = usePathname()

  const mutation = useMutation({
    mutationFn: (q: string) => fetchSearchResults(q, locale),
  })

  const debouncedSearch = useMemo(
    () =>
      debounce((q: string) => {
        if (q.trim().length > 0) {
          mutation.mutate(q.trim())
        }
      }, 500),
    // mutation.mutate is referentially stable per react-query contract
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale],
  )

  useEffect(() => () => debouncedSearch.clear(), [debouncedSearch])

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => inputRef.current?.focus(), 100)
      return () => clearTimeout(timer)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = ""
    }
  }, [open])

  useEffect(() => {
    if (open) onClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [open, onClose])

  useEffect(() => {
    if (!open) {
      setQuery("")
      mutation.reset()
    }
    // mutation.reset is referentially stable per react-query contract
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setQuery(value)
      debouncedSearch(value)
    },
    [debouncedSearch],
  )

  // Submitting (Enter) leaves the quick-search overlay and opens the full
  // /search results page with the query — the modal stays a fast preview.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && query.trim().length > 0) {
        debouncedSearch.clear()
        onClose()
        router.push(`/search?q=${encodeURIComponent(query.trim())}`)
      }
    },
    [query, debouncedSearch, onClose, router],
  )

  const handleItemClick = useCallback(() => {
    onClose()
  }, [onClose])

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose()
      }
    },
    [onClose],
  )

  if (!open) return null

  const hasResults = mutation.isSuccess && mutation.data.results.length > 0
  const hasNoResults =
    (mutation.isSuccess &&
      mutation.data.results.length === 0 &&
      query.trim().length > 0) ||
    (mutation.isError && query.trim().length > 0)
  const isLoading = mutation.isPending
  // The panel only turns into the full "results" box once a search is active;
  // before that it's just the compact input bar (EGDE-412 regression fix).
  const isExpanded = isLoading || hasResults || hasNoResults
  const totalCount = mutation.data?.total ?? 0

  const countLabel = t("totalSearchResults", { count: totalCount })

  return (
    <Overlay
      data-component="search"
      onClick={handleOverlayClick}
      role="presentation"
    >
      <SearchContainer
        role="dialog"
        aria-modal
        aria-label={t("searchSiteAria")}
      >
        <SearchPanel $expanded={isExpanded}>
          <SearchInputWrapper>
            <StyledInput
              ref={inputRef}
              type="search"
              placeholder={placeholder?.trim() || t("searchEgged")}
              aria-label={t("searchSiteAria")}
              value={query}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
            />
            <MobileCloseButton onClick={onClose} aria-label="Close search">
              <CloseIcon />
            </MobileCloseButton>
          </SearchInputWrapper>

          {isLoading && (
            <LoadingWrapper>
              <SearchIcon />
            </LoadingWrapper>
          )}

          {hasResults && (
            <Results
              results={mutation.data.results}
              countLabel={countLabel}
              onItemClick={handleItemClick}
            />
          )}

          {hasNoResults && <EmptyState query={query} />}
        </SearchPanel>

        <CloseButton onClick={onClose} aria-label="Close search">
          <CloseIcon />
        </CloseButton>
      </SearchContainer>
    </Overlay>
  )
}

export default Search
